import { ScimEntityService } from './ScimEntityService';
import { extractErrorMessage } from '../utils/apiUtils';

export interface BuildTimeData {
    [key: string]: any;
}

class BuildTimeService {
    private buildTimeData: BuildTimeData | null = null;
    private loading: boolean = false;
    private error: string | null = null;
    private listeners: Set<() => void> = new Set();
    private entityService: ScimEntityService;

    constructor() {
        this.entityService = new ScimEntityService();
    }

    /**
     * Fetch build time data from mgmt/buildtime endpoint
     * Safe operation - never throws, only sets error state
     */
    async fetchBuildTime(): Promise<BuildTimeData | null> {
        if (this.loading) {
            // If already loading, wait for it to complete
            return new Promise((resolve) => {
                const checkInterval = setInterval(() => {
                    if (!this.loading) {
                        clearInterval(checkInterval);
                        resolve(this.buildTimeData);
                    }
                }, 100);
            });
        }

        this.loading = true;
        this.error = null;

        try {
            const data = await this.entityService.getBuildTime();
            this.buildTimeData = data;
            this.error = null;
            this.notifyListeners();
            return data;
        } catch (error) {
            console.error('Error fetching build time:', error);
            // Use extractErrorMessage to get proper error message
            this.error = extractErrorMessage(error);
            this.buildTimeData = null;
            this.notifyListeners();
            // Never throw - this is a safe operation
            return null;
        } finally {
            this.loading = false;
        }
    }

    /**
     * Get cached build time data (returns null if not yet fetched)
     */
    getBuildTime(): BuildTimeData | null {
        return this.buildTimeData;
    }

    /**
     * Check if build time data is currently loading
     */
    isLoading(): boolean {
        return this.loading;
    }

    /**
     * Get the last error, if any
     */
    getError(): string | null {
        return this.error;
    }

    /**
     * Clear cached build time data
     */
    clearBuildTime(): void {
        this.buildTimeData = null;
        this.error = null;
        this.notifyListeners();
    }

    /**
     * Subscribe to changes in build time data
     */
    subscribe(listener: () => void): () => void {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }

    private notifyListeners(): void {
        this.listeners.forEach(listener => listener());
    }
}

export default new BuildTimeService();
